import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTable, useSortBy, usePagination } from 'react-table';
import classnames from 'classnames';

import { Pagination } from 'components/Pagination';

type Props = {
  columns: any;
  data?: any;
  title?: string;
  extraPadding?: boolean;
  hasPagination?: boolean;
  fixedFirstColumn?: boolean;
};

const getStyleBasedOnIndex = (i: number, style: string) => {
  return {
    paddingLeft: i == 1 ? style : 'none',
    width: i == 0 ? style : 'auto',
  };
};

export const DataTable: React.FunctionComponent<Props> = ({
  data,
  columns,
  hasPagination,
  title,
  extraPadding,
  fixedFirstColumn,
}) => {
  // using undefined where beacause we still want to display a table when data is empty
  if (data === undefined) {
    return null;
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 10 },
    },
    useSortBy,
    usePagination,
  );

  const body = hasPagination ? page : rows;

  const tableClasses = classnames({
    'nft-history-table table': true,
    'table--extra-padding': extraPadding,
    'table--fixed-first-column': fixedFirstColumn,
  });

  const [columnStyle, setColumnStyle] = React.useState('auto');

  useEffect(() => {
    const firstColumnHeader = document.getElementsByTagName('th')[0];
    const firstColumnWidth = firstColumnHeader && firstColumnHeader.offsetWidth;
    setColumnStyle(firstColumnWidth ? `${firstColumnWidth + 40}px` : 'auto');
  }, []);

  return (
    <>
      {!!title && (
        <h3 className={`table__title ${extraPadding && 'table__title--extra-padding'}`}>{title}</h3>
      )}
      <div className='table__wrapper'>
        <table {...getTableProps()} className={tableClasses}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, i) => {
                  const columnProps = {
                    ...column.getHeaderProps(column.getSortByToggleProps()),
                    style: getStyleBasedOnIndex(i, columnStyle),
                  };
                  return <th {...columnProps}>{column.render('Header')}</th>;
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {body.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell, i) => {
                    const cellProps = {
                      ...cell.getCellProps(),
                      style: getStyleBasedOnIndex(i, columnStyle),
                    };
                    return <td {...cellProps}>{cell.render('Cell')}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!!hasPagination && (
        <div className='d-flex justify-content-end'>
          <Pagination pageCount={pageCount} currentPage={pageIndex} onPageChange={gotoPage} />
        </div>
      )}
    </>
  );
};
